import React, { useEffect, useRef, useState } from "react";
import "./AthleteMonitoringSystem.css";
import {
  FaRunning,
  FaCalendarAlt,
  FaChartLine,
  FaTrophy,
  FaUsers,
  FaBolt,
  FaCheckCircle,
  FaExternalLinkAlt
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import amsImage from "../assets/amsss.png";

// You can import your tech images here, e.g.:
// import techImage1 from "../assets/ams-tech1.jpg";

const amsFeatures = [
  {
    id: "tracking",
    icon: <FaRunning />,
    tag: "Tracking",
    title: "Athlete Performance Tracking",
    short: "Monitor athlete progress and metrics in real-time.",
    desc: "Monitor athlete progress, workload, recovery, attendance, fitness scores, and performance metrics in real-time to ensure optimal development.",
    highlight: "Data-driven athlete growth.",
  },
  {
    id: "calendar",
    icon: <FaCalendarAlt />,
    tag: "Planning",
    title: "Smart Training Calendar",
    short: "Plan training sessions, mesocycles, and schedules.",
    desc: "Plan training sessions, mesocycles, microcycles, competitions, and schedules with an interactive calendar system tailored for sports academies.",
    highlight: "Never miss a training milestone.",
  },
  {
    id: "analytics",
    icon: <FaChartLine />,
    tag: "Insights",
    title: "Analytics & Reports",
    short: "Generate performance reports and workload analysis.",
    desc: "Generate performance reports, training summaries, workload analysis, and athlete insights with visual analytics for coaches and management.",
    highlight: "Visualize your team's success.",
  },
  {
    id: "competition",
    icon: <FaTrophy />,
    tag: "Management",
    title: "Competition Management",
    short: "Manage tournaments, schedules, and rankings.",
    desc: "Manage competitions, tournaments, registrations, schedules, rankings, and athlete participation efficiently from a single dashboard.",
    highlight: "Streamline your sporting events.",
  },
  {
    id: "collaboration",
    icon: <FaUsers />,
    tag: "Teamwork",
    title: "Team & Coach Collaboration",
    short: "Seamless communication for the entire organization.",
    desc: "Enable seamless communication and coordination between athletes, coaches, physiotherapists, and administrators to build a cohesive team environment.",
    highlight: "Connect your entire sports staff.",
  },
  {
    id: "updates",
    icon: <FaBolt />,
    tag: "Live",
    title: "Real-time Updates",
    short: "Instant notifications for schedules and performance.",
    desc: "Receive instant updates for schedules, workouts, performance changes, attendance, and important notifications directly on your device.",
    highlight: "Stay informed, instantly.",
  },
];

/* ── AMS Slideshow Data ── */
const amsSlideshowImages = [
  {
    // Replace with your local import, e.g. src: techImage1
    src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop",
    caption: "Data-Driven Performance",
    sub: "Real-time analytics for every training session",
  },
  {
    src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
    caption: "Comprehensive Dashboards",
    sub: "Visualize athlete workload and recovery metrics",
  },
  {
    src: "https://images.unsplash.com/photo-1584735935682-2f2b69dff9d2?q=80&w=1200&auto=format&fit=crop",
    caption: "Biomechanical Analysis",
    sub: "Track kinematic markers and identify injury risks",
  },
  {
    src: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop",
    caption: "Cloud-Synced Ecosystem",
    sub: "Seamless access for coaches, physios, and management",
  },
];

/* ── Scroll reveal helper ── */
function initScrollReveal() {
  const elements = document.querySelectorAll(".ams-reveal");
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("ams-visible");
          observer.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12 }
  );
  elements.forEach((el) => observer.observe(el));
}

/* ── Single Feature Card ── */
const AmsCard = ({ feature, index }) => {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.style.transitionDelay = `${index * 0.07}s`;
  }, [index]);

  return (
    <div ref={ref} className="ams-card">
      <div className="ams-card__header">
        <span className="ams-card__tag">{feature.tag}</span>
        <div className="ams-card__icon" aria-hidden="true">
          {feature.icon}
        </div>
      </div>

      <h3 className="ams-card__title">{feature.title}</h3>
      <p className="ams-card__short">{feature.short}</p>

      <div className={`ams-card__body${expanded ? " ams-card__body--open" : ""}`}>
        <p className="ams-card__desc">{feature.desc}</p>
        <div className="ams-card__highlight">
          <FaCheckCircle className="ams-card__highlight-icon" aria-hidden="true" />
          <span>{feature.highlight}</span>
        </div>
      </div>

      <div className="ams-card__footer">
        <button
          className="ams-btn-toggle"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? "Show Less ↑" : "Learn More →"}
        </button>
        <a href="https://ams.aikyamsports.com/" target="_blank" rel="noopener noreferrer" className="ams-link">
          <button className="ams-btn-visit">
            Access AMS <FaExternalLinkAlt style={{ fontSize: '10px', marginLeft: '4px' }}/>
          </button>
        </a>
      </div>
    </div>
  );
};

/* ── Image Slideshow Component ── */
const AmsSlideshow = () => {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setPrev(current);
      setTransitioning(true);
      setCurrent((c) => (c + 1) % amsSlideshowImages.length);
      setTimeout(() => {
        setPrev(null);
        setTransitioning(false);
      }, 900);
    }, 4500);
    return () => clearInterval(timer);
  }, [current]);

  const goTo = (i) => {
    if (i === current) return;
    setPrev(current);
    setTransitioning(true);
    setCurrent(i);
    setTimeout(() => { setPrev(null); setTransitioning(false); }, 900);
  };

  return (
    <div className="ams-slideshow ams-reveal">
      {prev !== null && (
        <div
          className="ams-slide ams-slide--out"
          style={{ backgroundImage: `url(${amsSlideshowImages[prev].src})` }}
        />
      )}
      <div
        key={current}
        className={`ams-slide ams-slide--in${transitioning ? " ams-slide--entering" : ""}`}
        style={{ backgroundImage: `url(${amsSlideshowImages[current].src})` }}
      >
        <div className="ams-slide__overlay" />
        <div className="ams-slide__caption">
          <span className="ams-slide__eyebrow">Aikyam Tech · AMS</span>
          <h3 className="ams-slide__title">{amsSlideshowImages[current].caption}</h3>
          <p className="ams-slide__sub">{amsSlideshowImages[current].sub}</p>
        </div>
      </div>
      <div className="ams-slide__dots">
        {amsSlideshowImages.map((_, i) => (
          <button
            key={i}
            className={`ams-slide__dot${i === current ? " ams-slide__dot--active" : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const AthleteMonitoringSystem = () => {
  useEffect(() => {
    initScrollReveal();
  }, []);

  return (
    <>
      <Helmet>
        <title>Aikyam AMS | Modern Athlete Management System</title>
        <meta
          name="description"
          content="Aikyam AMS is an intelligent Athlete Management System for athletes, coaches, and academies to streamline performance tracking, training, and analytics."
        />
        <link rel="canonical" href="https://aikyamsports.com/athlete_monitoring_system" />
      </Helmet>

      <section className="ams-section" aria-label="Aikyam AMS">
        
        {/* ─── Header ─── */}
        <div className="ams-header ams-reveal">
          <span className="ams-eyebrow">By Aikyam Sports Science</span>
          <h2 className="ams-heading">
            Welcome to <em>Aikyam AMS</em>
          </h2>
          <p className="ams-subtext">
            Aikyam AMS is a modern Athlete Management System developed to help athletes, coaches, academies, and sports organizations streamline performance tracking, training management, analytics, communication, and competition planning — all in one intelligent platform.
          </p>
          <div className="ams-header-actions mt-4">
             <a href="https://ams.aikyamsports.com/" target="_blank" rel="noopener noreferrer">
                <button className="ams-btn-visit ams-btn-visit--large">
                    Go to AMS Portal <FaExternalLinkAlt style={{ fontSize: '12px', marginLeft: '6px' }}/>
                </button>
             </a>
          </div>
        </div>

        {/* ─── New Tech Slideshow ─── */}
        <AmsSlideshow />

        {/* ─── Features Grid ─── */}
        <div className="ams-grid ams-reveal">
          {amsFeatures.map((feature, i) => (
            <AmsCard key={feature.id} feature={feature} index={i} />
          ))}
        </div>

        {/* ─── CTA Strip ─── */}
        <div className="ams-why ams-reveal" style={{ marginTop: "72px" }}>
          <div className="ams-why__left">
            <img
              src={amsImage}
              alt="Aikyam AMS Dashboard Preview"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=600&q=80";
              }}
            />
          </div>

          <div className="ams-why__right">
            <h3 className="ams-why__heading">Ready to elevate your team's performance?</h3>
            <p className="ams-why__sub">
              Join the future of sports management. Aikyam AMS provides all the tools you need under one digital roof.
            </p>

            <div className="ams-why-point">
              <FaCheckCircle className="ams-why-icon" aria-hidden="true" />
              <div>
                <h4>All-in-One Dashboard</h4>
                <p>Track fitness, attendance, and injuries in a single, intuitive interface.</p>
              </div>
            </div>

            <div className="ams-why-point">
              <FaCheckCircle className="ams-why-icon" aria-hidden="true" />
              <div>
                <h4>Built for Coaches & Academies</h4>
                <p>Designed specifically for the workflow of modern sports organizations.</p>
              </div>
            </div>

            <a href="https://ams.aikyamsports.com/" target="_blank" rel="noopener noreferrer" className="ams-link">
              <button className="ams-btn-visit ams-btn-visit--large" style={{marginTop: '15px'}}>
                Log In or Sign Up on AMS
              </button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default AthleteMonitoringSystem;